import {
  LIST_QUERY_KEY,
  DOCUMENTS_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'
import { DocumentSearchResult } from '@dc-shared-web/hooks/api/document/types'
import { InfiniteData, useQueryClient } from 'react-query'
import { GetMainTableQueryParams } from '@dc-shared-web/hooks/api/document/fetchMainTableDocuments'
import { useGetProcessingDocuments } from '@dc-shared-web/hooks/api/document/useGetProcessingDocuments'

export const usePollAndMergeProcessingCrunches = (
  processingUUIDs: string[],
  isLoading: boolean,
  currentTable: string,
  params: GetMainTableQueryParams
) => {
  const queryClient = useQueryClient()
  return useGetProcessingDocuments(
    {
      uuids: processingUUIDs,
    },
    processingUUIDs.length > 0,
    {
      onSuccess: (newProcessingData) => {
        // If the table is still loading, don't try to update the data.
        if (isLoading) return
        queryClient.setQueryData<
          InfiniteData<{ data: DocumentSearchResult[]; count: number }>
        >(
          [DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY, currentTable, params],
          (oldData) => {
            if (!oldData?.pages)
              //return empty infinite data if the old paginated data is not available
              return {
                pages: [],
                pageParams: [],
              }

            //check if there are any status changes in the processing crunches
            const hasStatusChanges = oldData.pages.some((page) =>
              page.data.some((doc) => {
                const updatedDoc = newProcessingData.find(
                  (processingDoc) => processingDoc.uuid === doc.uuid
                )
                // a processing crunch is found and the status has changed
                return updatedDoc && updatedDoc.status !== doc.status
              })
            )

            // if there are no status changes, return the old data
            if (!hasStatusChanges) return oldData

            //update the cache with the new processing statuses
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data.map((doc) => {
                  //find the crunches being processed
                  const updatedDoc = newProcessingData.find(
                    (processingDoc) => processingDoc.uuid === doc.uuid
                  )
                  //update the processing status of the crunch if it has changed
                  if (updatedDoc && updatedDoc.status !== doc.status) {
                    return { ...doc, status: updatedDoc.status }
                  }
                  //return the doc unchanged if the status has not changed
                  return doc
                }),
              })),
            }
          }
        )
      },
    }
  )
}
